import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { Box, Grid, TextField, Button, Typography } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { useTranslation } from 'react-i18next';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';

import { Loader } from 'ui';
import { addNotificationAction } from 'store/notifications/notificationsActions';

import { purchaseProducts, acceptTermsAndConditions } from '../services';
import { fetchShopHistoryAction, fetchShopProductsAction } from '../store/shopActions';
import { ShopTermsAndConditions } from './ShopTermsAndConditions';

const useStyle = makeStyles(() => ({
  button: {
    minWidth: 120,
    marginLeft: 10,
  },
}));

const validationSchema = Yup.object().shape({
  companyName: Yup.string().required(),
  address: Yup.string().required(),
  city: Yup.string().required(),
  zipCode: Yup.string().required(),
  country: Yup.string().required(),
  vatId: Yup.string().required(),
  contactPerson: Yup.string().required(),
});

const ShopCheckoutForm = ({ products, onSuccess, children }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const classes = useStyle();

  const productIds = products.map(({ id }) => id);

  const authData = useSelector(({ auth }) => auth.data);

  const [isLoading, setIsLoading] = useState(false);

  const [termsAccepted, setTermsAccepted] = useState(false);
  const [termsDocuments, setTermsDocuments] = useState(new Map([]));

  const initialValues =
    authData && authData.organisation
      ? {
          companyName: authData.organisation.name,
          address: authData.organisation.address.addressLine,
          city: authData.organisation.address.city,
          zipCode: authData.organisation.address.zip,
          country: authData.organisation.address.country.name,
          contactPerson: `${authData.firstName} ${authData.name}`,
        }
      : {};

  const handleTermsAccepted = terms => {
    setTermsAccepted(true);
    setTermsDocuments(terms);
  };

  const handleTermsRejected = () => {
    setTermsAccepted(false);
    setTermsDocuments(null);
  };

  const handleSubmit = values => {
    if (termsAccepted) {
      setIsLoading(true);

      const agreements = Object.entries(Object.fromEntries(termsDocuments)).map(([key, val]) => ({
        product: key,
        version: val.document?.id,
      }));

      acceptTermsAndConditions(agreements)
        .then(() => {
          purchaseProducts({
            ...values,
            productIds,
          })
            .then(response => {
              onSuccess(response);
              dispatch(fetchShopProductsAction());
              dispatch(fetchShopHistoryAction());
            })
            .catch(error =>
              dispatch(
                addNotificationAction({
                  message: error.message,
                  variant: 'error',
                }),
              ),
            )
            .finally(() => {
              setIsLoading(false);
            });
        })
        .catch(error => {
          dispatch(
            addNotificationAction({
              message: error.message,
              variant: 'error',
            }),
          );
          setIsLoading(false);
        });
    }
  };

  useEffect(() => {
    dispatch(fetchShopHistoryAction());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return isLoading ? (
    <Loader cover />
  ) : (
    <Formik
      onSubmit={handleSubmit}
      validationSchema={validationSchema}
      validateOnChange={false}
      initialValues={initialValues}
    >
      {({ handleChange, errors }) => (
        <>
          <Box pt={2}>
            <Box mt={4} mb={2} px={6}>
              <Typography variant="h5">{t('Please fill in your billing address')}</Typography>
            </Box>

            <Form>
              <Box width="75%" margin="0 auto" px={6}>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <TextField
                      name="companyName"
                      label={t('ENTITY.NAME')}
                      variant="outlined"
                      error={Boolean(errors.companyName)}
                      onChange={handleChange}
                      className={classes.input}
                      defaultValue={authData.organisation.name}
                      fullWidth
                      disabled={isLoading}
                    />
                  </Grid>

                  <Grid item xs={6}>
                    <TextField
                      name="address"
                      label={t('Address')}
                      variant="outlined"
                      error={Boolean(errors.address)}
                      onChange={handleChange}
                      className={classes.input}
                      defaultValue={authData.organisation.address.addressLine}
                      fullWidth
                      disabled={isLoading}
                    />
                  </Grid>

                  <Grid item xs={6}>
                    <TextField
                      name="city"
                      label={t('City')}
                      variant="outlined"
                      error={Boolean(errors.city)}
                      onChange={handleChange}
                      className={classes.input}
                      defaultValue={authData.organisation.address.city}
                      fullWidth
                      disabled={isLoading}
                    />
                  </Grid>

                  <Grid item xs={6}>
                    <TextField
                      name="zipCode"
                      label={t('Zip code')}
                      variant="outlined"
                      error={Boolean(errors.zipCode)}
                      onChange={handleChange}
                      className={classes.input}
                      defaultValue={authData.organisation.address.zip}
                      fullWidth
                      disabled={isLoading}
                    />
                  </Grid>

                  <Grid item xs={6}>
                    <TextField
                      name="country"
                      label={t('Country')}
                      variant="outlined"
                      error={Boolean(errors.country)}
                      onChange={handleChange}
                      className={classes.input}
                      defaultValue={authData.organisation.address.country.name}
                      fullWidth
                      disabled={isLoading}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <TextField
                      name="vatId"
                      label={t('Vat ID')}
                      variant="outlined"
                      error={Boolean(errors.vatId)}
                      onChange={handleChange}
                      className={classes.input}
                      fullWidth
                      disabled={isLoading}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <TextField
                      name="contactPerson"
                      label={t('Contact person')}
                      variant="outlined"
                      error={Boolean(errors.contactPerson)}
                      onChange={handleChange}
                      className={classes.input}
                      defaultValue={`${authData.firstName} ${authData.name}`}
                      fullWidth
                      disabled={isLoading}
                    />
                  </Grid>
                </Grid>
                <ShopTermsAndConditions
                  products={products}
                  onAccepted={handleTermsAccepted}
                  onRejected={handleTermsRejected}
                />
              </Box>
              <Box p={2} pt={4}>
                <Grid container justifyContent="flex-end">
                  {children && <Grid item>{children}</Grid>}
                  <Grid item>
                    <Button
                      variant="contained"
                      color="primary"
                      className={classes.button}
                      type="submit"
                      disabled={isLoading || !termsAccepted}
                    >
                      {t('Buy')}
                    </Button>
                  </Grid>
                </Grid>
              </Box>
            </Form>
          </Box>
        </>
      )}
    </Formik>
  );
};

ShopCheckoutForm.defaultProps = {
  children: null,
};

ShopCheckoutForm.propTypes = {
  onSuccess: PropTypes.func.isRequired,
  products: PropTypes.arrayOf(PropTypes.object).isRequired,
  children: PropTypes.node,
};

export { ShopCheckoutForm };
